import { useEffect, useState } from 'react';
import { modImageUrl } from '../api/servers';
import { ca } from '../i18n/ca';
import { hueOf } from './missionCovers';
import { MOSAIC_CAPACITY, modInitials, mosaicMods, type ServerMod } from './serverMods';

/**
 * One mod as a picture: its Steam Workshop preview over a tile of its own
 * colour with its initials, so a mod with no picture (none on the Workshop, no
 * Workshop id, a platform that cannot reach Steam) is a tile like the others
 * and never a hole.
 *
 * The platform answers the preview from its cache and 404 while it is fetching
 * it (`ModImageService`), so a 404 is usually "not yet": this asks again,
 * twice, a few seconds apart, as `TerrainImage` does, and then leaves the
 * initials.
 */
export function ModTile({ mod, className = 'mod-tile' }: { mod: ServerMod; className?: string }) {
  const [attempt, setAttempt] = useState(0);
  const [broken, setBroken] = useState(false);

  // While rendering, not in an effect: an effect also runs on mount, after the
  // first commit, and wiped a failure reported before it ran.
  const [seen, setSeen] = useState(mod.id);
  if (seen !== mod.id) {
    setSeen(mod.id);
    setAttempt(0);
    setBroken(false);
  }

  useEffect(() => {
    if (!broken || attempt >= 2) return undefined;
    const timer = setTimeout(
      () => {
        setBroken(false);
        setAttempt((n) => n + 1);
      },
      4000 * (attempt + 1),
    );
    return () => clearTimeout(timer);
  }, [broken, attempt]);

  const hue = hueOf(mod.name);
  const url = mod.id ? modImageUrl(mod.id) : null;
  return (
    <span
      className={className}
      title={mod.name}
      style={{
        background: `linear-gradient(150deg, hsl(${hue} 30% 24%), hsl(${(hue + 40) % 360} 26% 13%))`,
      }}
    >
      <span className="mod-initials" aria-hidden="true">
        {modInitials(mod.name)}
      </span>
      {url && !broken && (
        <img
          src={attempt === 0 ? url : `${url}?try=${attempt}`}
          alt=""
          loading="lazy"
          decoding="async"
          onError={() => setBroken(true)}
        />
      )}
    </span>
  );
}

/**
 * A server's mods as a regular grid of equal square tiles (the owner,
 * 2026-10-13: the mosaic of unequal tiles showed few mods and was ugly): the
 * Workshop previews in the server's own order, main mods first, up to
 * `MOSAIC_CAPACITY`, and when there are more the last place is a "+N" tile
 * with the count of the rest. A server with no Workshop mods is vanilla Arma 3
 * and shows the game's logo alone (a mask painted in the text colour, so it
 * reads on both themes). Creator DLCs have no preview of their own and are not
 * mods here, so a server that runs only those is vanilla too.
 *
 * It is a list with its own accessible name; the mods are named, one by one,
 * where the page lists them.
 */
export function ModMosaic({ mods, wide = false }: { mods: ServerMod[]; wide?: boolean }) {
  const t = ca.serversPage;
  if (mods.length === 0) {
    return (
      <ul className="mod-mosaic vanilla" aria-label={t.vanillaLabel}>
        <li className="mod-tile arma3" aria-label={t.vanillaLabel}>
          <span className="arma3-logo" aria-hidden="true" />
        </li>
      </ul>
    );
  }
  const { tiles, more } = mosaicMods(mods, wide ? MOSAIC_CAPACITY.wide : MOSAIC_CAPACITY.card);
  return (
    <ul className={`mod-mosaic${wide ? ' wide' : ''}`} aria-label={t.modsTilesLabel}>
      {tiles.map((mod, index) => (
        <li key={mod.id ?? `${mod.name}-${index}`}>
          <ModTile mod={mod} />
        </li>
      ))}
      {more > 0 && (
        <li className="mod-tile more" aria-label={t.modsMore(more)} title={t.modsMore(more)}>
          <span aria-hidden="true">+{more}</span>
        </li>
      )}
    </ul>
  );
}
